// Aba "Códigos dos tribunais" (F107): estados do cartão, motivo do erro em linguagem simples e quando buscar de novo.

export const CODE_STATUS = {
    none: { tone: 'neutral', label: 'Sem código' },
    activating: { tone: 'brand', label: 'Ativando…' },
    active: { tone: 'success', label: 'Ativo' },
    error: { tone: 'danger', label: 'Com erro' },
};

// `fix` diz o que o botão do cartão faz: refazer o passo a passo, pedir a ativação de novo, reconectar pelo print
// (tribunal sem ativação automática) ou trocar o certificado na aba Protocolo. `team` = a equipe resolve, sem botão.
const FAILURES = {
    totp_not_configured: {
        title: 'O código de acesso do PJe não está ativo',
        action: 'A Facilita precisa ativar o código no tribunal antes de protocolar.',
        fix: 'activate',
        label: 'Ativar código',
    },
    still_active: {
        title: 'O código antigo ainda está ativo no PDPJ',
        action: 'Refaça o passo a passo entrando pelo "Entrar com PDPJ" e clique em "Já desativei".',
        fix: 'guide',
        label: 'Refazer o passo a passo',
    },
    login_failed: {
        title: 'O tribunal não aceitou o seu certificado',
        action: 'Confira se o certificado está cadastrado no tribunal e tente de novo. Se ele mudou, envie o certificado novo.',
        fix: 'activate',
        label: 'Tentar de novo',
    },
    certificate_unreadable: {
        title: 'Não conseguimos abrir o seu certificado',
        action: 'Envie o certificado de novo.',
        fix: 'certificate',
        label: 'Enviar certificado',
    },
    court_unavailable: {
        title: 'O tribunal ficou fora do ar enquanto ativávamos',
        action: 'Tente de novo daqui a pouco.',
        fix: 'activate',
        label: 'Tentar de novo',
    },
    needs_reconnect: {
        title: 'O tribunal recusou o código guardado',
        action: 'Isso acontece quando o código é refeito no tribunal. Conecte de novo.',
        fix: 'activate',
        label: 'Conectar de novo',
    },
    totp_setup_not_offered: {
        title: 'O tribunal não ofereceu o cadastro do código',
        action: 'O tribunal não ofereceu o cadastro do código. Nossa equipe vai verificar.',
        fix: 'team',
    },
};

const UNEXPECTED = {
    title: 'Não conseguimos ativar o código',
    action: 'Tente de novo. Se continuar, fale com a gente pelo chat.',
    fix: 'activate',
    label: 'Tentar de novo',
};

// Sem ativação automática (eproc), toda correção é reconectar pelo print do QR Code.
export const failureCopyOf = (item) => {
    const reasonCode = item?.failure?.reasonCode;
    const copy = FAILURES[reasonCode] || UNEXPECTED;
    if (item?.autoActivation || copy.fix === 'certificate' || copy.fix === 'team')
        return copy;
    return { ...copy, fix: 'connect', label: 'Conectar de novo', action: reasonCode === 'needs_reconnect' ? copy.action : 'Conecte de novo com o QR Code do tribunal.' };
};

// Enquanto um código está sendo ativado, olhamos a cada minuto (o robô passa a cada ~2 min).
export const ACTIVATING_REFRESH_MS = 60 * 1000;
// Depois de 10 minutos sem o cliente pedir, paramos de buscar: cada leitura do código vai para a trilha de acessos.
export const AUTO_REFRESH_WINDOW_MS = 10 * 60 * 1000;
// Margem para o servidor já estar na janela nova quando buscarmos.
const EXPIRY_MARGIN_MS = 800;

// Com a hora do servidor na resposta (serverTime), a diferença de relógio sai exata: meio da ida e volta.
export const serverOffsetOf = (serverTime, sentAt, receivedAt) => {
    const at = serverTime ? new Date(serverTime).getTime() : NaN;
    return Number.isFinite(at) ? at - (sentAt + receivedAt) / 2 : null;
};

// Sem serverTime: diferença entre o relógio do servidor e o daqui (ms). Começa em 0 e só se corrige quando a resposta prova que o
// relógio daqui está errado: código que chega já vencido (adiantado) ou com mais de uma janela pela frente (atrasado).
export const clockOffsetOf = (codes = [], receivedAt, current = 0) => codes.reduce((offset, item) => {
    const expiresAt = item?.code && item.expiresAt ? new Date(item.expiresAt).getTime() : NaN;
    if (!Number.isFinite(expiresAt))
        return offset;
    const periodMs = (Number(item.period) || 30) * 1000;
    const left = expiresAt - (receivedAt + offset);
    if (left <= 0)
        return expiresAt - receivedAt - 500;
    if (left > periodMs + 2000)
        return expiresAt - receivedAt - periodMs;
    return offset;
}, current);

// Quando este código vence no relógio daqui, já com a diferença de relógio corrigida.
export const localExpiryOf = (item, offset = 0) => {
    if (!item?.code || !item.expiresAt)
        return null;
    const expiresAt = new Date(item.expiresAt).getTime();
    return Number.isFinite(expiresAt) ? expiresAt - offset : null;
};

// Próxima busca (ms a partir de agora): no vencimento mais próximo ou, com ativação em curso, em 1 minuto.
export const nextRefreshDelayOf = (codes = [], now = Date.now()) => {
    const candidates = [];
    codes.forEach(item => {
        if (item.status === 'active' && item.localExpiresAt)
            candidates.push(item.localExpiresAt - now + EXPIRY_MARGIN_MS);
        if (item.status === 'activating')
            candidates.push(ACTIVATING_REFRESH_MS);
    });
    if (!candidates.length)
        return null;
    return Math.max(1000, Math.min(...candidates));
};

export const secondsLeftOf = (item, now = Date.now()) =>
    item?.localExpiresAt ? Math.max(0, Math.ceil((item.localExpiresAt - now) / 1000)) : 0;

// "123456" vira "123 456"; a chave em texto vai em blocos de 4.
export const formatCode = (code) => String(code || '').replace(/^(\d{3})(\d{3})$/, '$1 $2');

export const groupSecret = (secret) => String(secret || '').replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');

// O mesmo item da aba Códigos, montado com os metadados e o acesso de /filing/settings (sem ler segredos).
export const codeItemOf = (access, certificate) => {
    const stored = certificate.access?.[access.key] || {};
    const statuses = { ready: 'active', activating: 'activating', activation_failed: 'error', reconnect: 'error' };
    return {
        ...access,
        certificateId: certificate.id,
        accessGroup: access.key,
        status: statuses[stored.status] || 'none',
        failure: stored.failure || (stored.status === 'reconnect' ? { reasonCode: 'needs_reconnect' } : null),
        lastError: stored.lastError,
    };
};
